// The browser-side world: real actors (Ed25519 keys), one real witnessed Scope running the real marketplace policy,
// and a journal of every submission so the whole history is replayed on reload (kernel invariant I6).
// Every submission gets a receipt signed by the community's witness key; replay must reproduce each one.
// Private messages are signed claims kept on "devices" (this.device) and never submitted to the scope.
// Only the kernel's declared public surface (model/src/index.ts) is imported.

import { ReplayError, Scope, b64u, newActor, receiptBody, sign } from '@model/index.ts';
import type { Actor, ActorId, Claim, Decision, Draft } from '@model/index.ts';
import { CAST } from './cast.ts';
import type { CastMember } from './cast.ts';
import { setLang } from './i18n.ts';
import type { Lang } from './i18n.ts';
import * as store from './store.ts';

export const MIN = 60_000;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;

export interface Person extends CastMember {
  actor: Actor;
  id: ActorId;
}

export interface Submission {
  claim: Claim;
  at: number;
  decision: Decision; // as the author receives it: redacted to what they may see, with the witness receipt
  operator: Decision; // the unredacted evaluation, for the record pane's operator view
}

export interface Audit {
  ok: boolean;
  receipts: number;
  detail: string;
}

// A denial a person was shown before they tried (capability is a question, not a stored flag).
export interface Check {
  at: number;
  viewer: ActorId;
  predicate: string;
  decision: Decision;
  summary: string;
}

interface Saved {
  version: 5;
  people: { key: string; privateKey: CryptoKey; id: ActorId; name?: { en: string; hi: string } }[];
  journal: { claim: Claim; at: number; receipt: string | null }[];
  device: Claim[];
  phones: string[];
  skew: number;
  seat: string;
  lang: Lang;
}

type DraftNoTime = Omit<Draft, 'issuedAt'>;

export class World extends EventTarget {
  scope!: Scope;
  people: Person[] = [];
  byId = new Map<ActorId, Person>();
  byKey = new Map<string, Person>();
  submissions: Submission[] = [];
  device: Claim[] = [];
  phones: string[] = [];
  checks: Check[] = [];
  skew = 0;
  seat = 'ananya';
  language: Lang = 'en';
  #lastAt = 0;
  #saving: Promise<unknown> = Promise.resolve();
  // On reload: how many stored receipts the fresh replay reproduced byte for byte.
  replayed: { receipts: number; matched: number } | null = null;

  now(): number {
    return Date.now() + this.skew;
  }

  #stamp(at?: number): number {
    const t = Math.max(at ?? this.now(), this.#lastAt + 1);
    this.#lastAt = t;
    return t;
  }

  p(key: string): Person {
    const x = this.byKey.get(key);
    if (!x) throw new Error(`no person ${key}`);
    return x;
  }

  get me(): Person {
    return this.p(this.seat);
  }

  name(id: ActorId | null | undefined, l: Lang = this.language): string {
    if (!id) return '';
    return this.byId.get(id)?.name[l] ?? `${id.slice(8, 14)}…`;
  }

  gender(id: ActorId | null | undefined): 'm' | 'f' {
    return (id && this.byId.get(id)?.g) || 'm';
  }

  // Sign as a person and submit to the scope. Every claim is bound to this scope (v2 scope field).
  // The decision is the scope's, untouched; the operator view is the same evaluation before redaction.
  async act(key: string, draft: DraftNoTime, at?: number): Promise<{ claim: Claim; d: Decision }> {
    const who = this.p(key);
    const t = this.#stamp(at);
    const claim = await sign(who.actor, { ...draft, scope: draft.scope ?? this.scope.id, issuedAt: t });
    const operator = this.scope.evaluate(claim, t);
    const d = await this.scope.submit(claim, t);
    this.submissions.push({ claim, at: t, decision: d, operator });
    this.changed();
    return { claim, d };
  }

  // Capability = "would this claim be admitted now?" Nothing is stored. The answer is cut to what the
  // requester may see (by default the person asking about their own draft).
  would(key: string, draft: DraftNoTime, requester?: ActorId): Decision {
    const now = this.now();
    const author = this.p(key).id;
    return this.scope.would(author, { ...draft, scope: draft.scope ?? this.scope.id, issuedAt: now }, now, requester ?? author);
  }

  receiptOf(s: Submission): Claim | undefined {
    return s.decision.receipt ?? this.scope.entry(s.claim.id)?.receipt;
  }

  // The audit path: a fresh replica holding no key re-verifies every receipt signature and the chain,
  // and re-runs every rule at its receipted time.
  async audit(): Promise<Audit> {
    const history = this.scope.history();
    const genesis = history[0]?.claim;
    if (!genesis || !this.scope.witness) return { ok: false, receipts: 0, detail: 'no witnessed history' };
    try {
      await Scope.replay(genesis, history, this.scope.witness);
      return { ok: true, receipts: history.length, detail: 'every receipt verifies, the chain is unbroken, and every decision re-ran the same' };
    } catch (e) {
      return { ok: false, receipts: history.length, detail: e instanceof ReplayError ? e.message : String(e) };
    }
  }

  noteCheck(viewer: ActorId, predicate: string, decision: Decision, summary: string): void {
    const sig = `${viewer}|${predicate}|${summary}`;
    if (this.checks.some((c) => `${c.viewer}|${c.predicate}|${c.summary}` === sig)) return;
    this.checks.push({ at: this.now(), viewer, predicate, decision, summary });
    queueMicrotask(() => this.dispatchEvent(new Event('checks')));
  }

  // A private message: signed (so it can become evidence later), delivered to the parties' devices only.
  async message(fromKey: string, to: ActorId, inquiry: string, text: string, at?: number): Promise<Claim> {
    const who = this.p(fromKey);
    const t = this.#stamp(at);
    const c = await sign(who.actor, { predicate: 'say', audience: [to], refs: [inquiry], body: { text }, issuedAt: t });
    this.device.push(c);
    this.changed();
    return c;
  }

  // The phone issuer keeps a keyed fingerprint so one number cannot verify many keys; never the number.
  async phoneFingerprint(digits: string): Promise<string> {
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`gulmohar-issuer:${digits}`));
    return b64u(bytes);
  }

  quiet = false;

  changed(): void {
    if (this.quiet) return;
    this.dispatchEvent(new Event('change'));
    this.persist();
  }

  persist(): void {
    const saved: Saved = {
      version: 5,
      people: this.people.map((x) => ({ key: x.key, privateKey: x.actor.privateKey, id: x.id, name: x.name })),
      journal: this.submissions.map((s) => ({ claim: s.claim, at: s.at, receipt: this.receiptOf(s)?.id ?? null })),
      device: this.device,
      phones: this.phones,
      skew: this.skew,
      seat: this.seat,
      lang: this.language,
    };
    this.#saving = this.#saving.then(() => store.save(saved));
  }

  flush(): Promise<unknown> {
    return this.#saving;
  }

  setSeat(key: string): void {
    this.seat = key;
    this.changed();
  }

  setLanguage(l: Lang): void {
    this.language = l;
    setLang(l);
    this.changed();
  }

  advance(ms: number): void {
    this.skew += ms;
    this.changed();
  }

  #index(): void {
    this.byId = new Map(this.people.map((x) => [x.id, x]));
    this.byKey = new Map(this.people.map((x) => [x.key, x]));
  }

  async #fresh(): Promise<void> {
    this.people = await Promise.all(
      CAST.map(async (c) => {
        const actor = await newActor();
        return { ...c, actor, id: actor.id };
      }),
    );
    this.#index();
  }

  // Rebuild from the device: same claims at the same times give the same decisions.
  async #replay(saved: Saved): Promise<void> {
    this.people = saved.people.map((s) => {
      const c = CAST.find((x) => x.key === s.key);
      if (!c) throw new Error('cast changed');
      return { ...c, name: s.name ?? c.name, actor: { id: s.id, privateKey: s.privateKey }, id: s.id };
    });
    this.#index();
    const [genesis, ...rest] = saved.journal;
    if (!genesis) throw new Error('empty journal');
    this.submissions = [];
    await this.genesis(genesis.claim, genesis.at);
    let matched = this.scope.entry(genesis.claim.id)?.receipt?.id === genesis.receipt ? 1 : 0;
    for (const s of rest) {
      const operator = this.scope.evaluate(s.claim, s.at);
      const decision = await this.scope.submit(s.claim, s.at);
      if (decision.receipt?.id === s.receipt) matched += 1;
      this.submissions.push({ claim: s.claim, at: s.at, decision, operator });
    }
    this.replayed = { receipts: saved.journal.length, matched };
    this.device = saved.device;
    this.phones = saved.phones;
    this.skew = saved.skew;
    this.seat = saved.seat;
    this.language = saved.lang;
    this.#lastAt = Math.max(0, ...saved.journal.map((j) => j.at), ...saved.device.map((d) => d.issuedAt));
  }

  async boot(seed: (w: World) => Promise<void>, opts: { fresh?: boolean } = {}): Promise<void> {
    const saved = opts.fresh ? undefined : await store.load<Saved>();
    if (saved?.version === 5) {
      try {
        await this.#replay(saved);
        setLang(this.language);
        return;
      } catch {
        // Fall through to a fresh neighbourhood if the saved world no longer replays.
      }
    }
    await this.#fresh();
    this.submissions = [];
    this.device = [];
    this.phones = [];
    this.checks = [];
    this.skew = 0;
    this.replayed = null;
    this.#lastAt = 0;
    this.quiet = true;
    try {
      await seed(this);
    } finally {
      this.quiet = false;
    }
    setLang(this.language);
    this.persist();
  }

  // Genesis is the only claim admitted by definition. The community's own key witnesses: it signs a receipt
  // for every submission, admitted or refused, chained to the one before.
  async genesis(policy: Claim, at: number): Promise<void> {
    this.scope = await Scope.genesis(policy, at, this.p('community').actor);
    this.#lastAt = at;
    const receipt = this.scope.entry(policy.id)?.receipt;
    const decision: Decision = { admitted: true, reason: 'admitted', seq: 0, trace: [], evidence: [], ...(receipt ? { receipt } : {}) };
    this.submissions.push({ claim: policy, at, decision, operator: decision });
  }

  async reset(seed: (w: World) => Promise<void>): Promise<void> {
    await store.clear();
    await this.boot(seed, { fresh: true });
    this.changed();
  }
}

export const world = new World();
export { receiptBody };
